import React, { useState } from 'react';
import {
  View, Text, TouchableOpacity, StyleSheet, Platform,
  Modal,
} from 'react-native';
import DateTimePicker, { DateTimePickerEvent } from '@react-native-community/datetimepicker';
import { useLanguage } from '../contexts/LanguageContext';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function padTwo(n: number): string {
  return n.toString().padStart(2, '0');
}

/** Convert DD.MM.YYYY string to a Date (or today if invalid). */
function parseDMY(value: string): Date {
  const parts = value.split('.');
  if (parts.length === 3) {
    const [dd, mm, yyyy] = parts.map(Number);
    if (dd && mm && yyyy) {
      const d = new Date(yyyy, mm - 1, dd);
      if (!isNaN(d.getTime())) return d;
    }
  }
  return new Date();
}

/** Convert HH:MM string to a Date (using today's date). */
function parseHHMM(value: string): Date {
  const parts = value.split(':');
  const d = new Date();
  if (parts.length === 2) {
    const [hh, mm] = parts.map(Number);
    if (!isNaN(hh) && !isNaN(mm)) {
      d.setHours(hh, mm, 0, 0);
    }
  }
  return d;
}

/** Format a Date to DD.MM.YYYY. */
function formatDMY(d: Date): string {
  return `${padTwo(d.getDate())}.${padTwo(d.getMonth() + 1)}.${d.getFullYear()}`;
}

/** Format a Date to HH:MM. */
function formatHHMM(d: Date): string {
  return `${padTwo(d.getHours())}:${padTwo(d.getMinutes())}`;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface Props {
  mode: 'date' | 'time';
  /** App-internal format: DD.MM.YYYY for date, HH:MM for time */
  value: string;
  onChange: (newValue: string) => void;
  /** Label shown above the input */
  label?: string;
  style?: object;
  /** Minimum selectable date (date mode only) */
  minDate?: Date;
  /** Maximum selectable date (date mode only) */
  maxDate?: Date;
}

// ---------------------------------------------------------------------------
// Web implementation (HTML native input)
// ---------------------------------------------------------------------------

function WebInput({ mode, value, onChange, label, style, minDate, maxDate }: Props) {
  // Convert app internal format → HTML input value format
  const toHtmlValue = (): string => {
    if (mode === 'date') {
      const parts = value.split('.');
      if (parts.length === 3) {
        const [dd, mm, yyyy] = parts;
        if (dd && mm && yyyy) return `${yyyy}-${mm}-${dd}`;
      }
      return '';
    }
    // time: already HH:MM
    return value;
  };

  const toHtmlDate = (d?: Date): string | undefined => {
    if (!d || mode !== 'date') return undefined;
    const yyyy = d.getFullYear();
    const mm = padTwo(d.getMonth() + 1);
    const dd = padTwo(d.getDate());
    return `${yyyy}-${mm}-${dd}`;
  };

  const fromHtmlValue = (v: string) => {
    if (mode === 'date') {
      if (!v) return;
      const parts = v.split('-');
      if (parts.length === 3) {
        const [yyyy, mm, dd] = parts;
        onChange(`${dd}.${mm}.${yyyy}`);
      }
    } else {
      onChange(v);
    }
  };

  return (
    <View style={[styles.wrapper, style]}>
      {label ? <Text style={styles.label}>{label}</Text> : null}
      {/* @ts-ignore – we're on web, the `<input>` element is valid */}
      <input
        type={mode}
        value={toHtmlValue()}
        min={toHtmlDate(minDate)}
        max={toHtmlDate(maxDate)}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => fromHtmlValue(e.target.value)}
        style={{
          border: '1px solid #E2E8F0',
          borderRadius: 10,
          padding: '12px',
          fontSize: 15,
          color: '#1E293B',
          backgroundColor: '#FFF',
          width: '100%',
          boxSizing: 'border-box',
          fontFamily: 'inherit',
          marginBottom: 16,
        }}
      />
    </View>
  );
}

// ---------------------------------------------------------------------------
// Native implementation (@react-native-community/datetimepicker)
// ---------------------------------------------------------------------------

function NativeInput({ mode, value, onChange, label, style, minDate, maxDate }: Props) {
  const { t } = useLanguage();
  const [pickerOpen, setPickerOpen] = useState(false);

  const currentDate = mode === 'date' ? parseDMY(value) : parseHHMM(value);

  const displayValue = value;

  const handleChange = (_event: DateTimePickerEvent, selectedDate?: Date) => {
    setPickerOpen(false);
    if (!selectedDate) return;
    if (mode === 'date') {
      onChange(formatDMY(selectedDate));
    } else {
      onChange(formatHHMM(selectedDate));
    }
  };

  return (
    <View style={[styles.wrapper, style]}>
      {label ? <Text style={styles.label}>{label}</Text> : null}
      <TouchableOpacity
        style={[styles.nativeBtn, style]}
        onPress={() => setPickerOpen(true)}
        activeOpacity={0.8}
      >
        <Text style={styles.nativeBtnText}>{displayValue}</Text>
        <Text style={styles.nativeIcon}>{mode === 'date' ? '📅' : '🕐'}</Text>
      </TouchableOpacity>

      {pickerOpen && Platform.OS === 'android' && (
        <DateTimePicker
          value={currentDate}
          mode={mode}
          display="default"
          minimumDate={mode === 'date' ? minDate : undefined}
          maximumDate={mode === 'date' ? maxDate : undefined}
          onChange={handleChange}
        />
      )}

      {pickerOpen && Platform.OS === 'ios' && (
        <Modal
          transparent
          animationType="slide"
          visible={pickerOpen}
          onRequestClose={() => setPickerOpen(false)}
        >
          <View style={styles.iosOverlay}>
            <View style={styles.iosSheet}>
              <TouchableOpacity
                style={styles.iosDone}
                onPress={() => setPickerOpen(false)}
              >
                <Text style={styles.iosDoneText}>{t('common.done')}</Text>
              </TouchableOpacity>
              <DateTimePicker
                value={currentDate}
                mode={mode}
                display="spinner"
                minimumDate={mode === 'date' ? minDate : undefined}
                maximumDate={mode === 'date' ? maxDate : undefined}
                onChange={(_evt, d) => {
                  if (!d) return;
                  if (mode === 'date') {
                    onChange(formatDMY(d));
                  } else {
                    onChange(formatHHMM(d));
                  }
                }}
              />
            </View>
          </View>
        </Modal>
      )}
    </View>
  );
}

// ---------------------------------------------------------------------------
// Exported component – picks the right implementation
// ---------------------------------------------------------------------------

export default function DateTimePickerInput(props: Props) {
  if (Platform.OS === 'web') {
    return <WebInput {...props} />;
  }
  return <NativeInput {...props} />;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create({
  wrapper: {},
  label: { fontSize: 13, fontWeight: '600', color: '#64748B', marginBottom: 6 },
  nativeBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 10,
    padding: 12,
    backgroundColor: '#FFF',
    marginBottom: 16,
  },
  nativeBtnText: { fontSize: 15, color: '#1E293B' },
  nativeIcon: { fontSize: 18 },

  // iOS modal picker
  iosOverlay: {
    flex: 1,
    justifyContent: 'flex-end',
    backgroundColor: 'rgba(0,0,0,0.4)',
  },
  iosSheet: { backgroundColor: '#FFF', borderTopLeftRadius: 20, borderTopRightRadius: 20 },
  iosDone: { alignItems: 'flex-end', padding: 16 },
  iosDoneText: { fontSize: 16, color: '#2563EB', fontWeight: '700' },
});
